import { Fragment } from 'react';
import { Layers, BookOpen, Presentation, Shield, Users, ListChecks } from 'lucide-react-native';
import { RailToolButton } from '../navigation/SidebarNav';
import { useUIStore, selectMapKeyShown } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useAuthStore } from '../../store/authStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { usePlanSurfaceOpen } from '../../hooks/usePlanSurfaceOpen';
import { PROGRAM_PANEL_ENABLED } from '../../constants/program';

/**
 * The map-tools rows pinned to the bottom of the desktop map route's
 * rail (Sept 2026 A2 — freeing up the map by moving the Key, the defense
 * plan entry, Present observations and the Layers toggle off it and into
 * the left nav). Wired into AppShell from app/_layout.tsx as
 * `<AppShell mapTools={<MapRailTools />}>`; imported `.web` like the pills
 * this replaces used to be (app/(tabs)/index.tsx) — no native rendering,
 * so this file owns its own store subscriptions rather than SidebarNav
 * knowing anything about the map.
 *
 * Active styling is a persistent fill, not the shared sliding rail the main
 * tabs use — three of these can be lit at once (e.g. Layers open while the
 * plan editor is also open), which a single "current route" pointer can't
 * represent.
 */
export function MapRailTools() {
  const layersPanelOpen = useUIStore((s) => s.layersPanelOpen);
  const toggleLayersPanel = useUIStore((s) => s.toggleLayersPanel);
  const peoplePanelOpen = useUIStore((s) => s.peoplePanelOpen);
  const togglePeoplePanel = useUIStore((s) => s.togglePeoplePanel);
  // People lists belong to an account: no button for signed-out visitors or
  // anonymous Fire Scouts, who can't write them.
  const canUsePeople = useAuthStore((s) => s.user != null && !s.user.isAnonymous);
  const programPanelOpen = useUIStore((s) => s.programPanelOpen);
  const toggleProgramPanel = useUIStore((s) => s.toggleProgramPanel);
  // The program brief is a facilitator's tool: the moderator claim, the same
  // gate generateAreaBrief and the areaBriefs rules use.
  const canUseProgram = useAuthStore((s) => s.isModerator) && PROGRAM_PANEL_ENABLED;
  const mapKeyShown = useUIStore(selectMapKeyShown);
  const toggleMapKey = useUIStore((s) => s.toggleMapKey);
  const presentationActive = useUIStore((s) => s.presentationActive);
  const requestSlideshowOpen = useUIStore((s) => s.requestSlideshowOpen);
  const requestSlideshowClose = useUIStore((s) => s.requestSlideshowClose);
  const planPanelOpen = useUIStore((s) => s.planPanelOpen);
  const closePlanPanel = useUIStore((s) => s.closePlanPanel);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  const requestPlanPanelOpen = useUIStore((s) => s.requestPlanPanelOpen);
  const clearViewedPlanNotice = useDefensePlansStore((s) => s.clearViewedPlanNotice);
  const { viewerOpen } = usePlanSurfaceOpen();
  const { requireAuth } = useAuthGate();

  const handlePresentPress = () => {
    if (presentationActive) requestSlideshowClose();
    else requestSlideshowOpen();
  };

  // Same three-way branch DefensePlanViewer.web.tsx's own close button and
  // DefensePlanPanel.web.tsx's own close button each use — this row is a
  // third way to reach either, not a new behavior.
  const handlePlanPress = () => {
    if (planPanelOpen) {
      closePlanPanel();
    } else if (viewerOpen) {
      setViewedPlanId(null);
      clearViewedPlanNotice();
    } else {
      requireAuth(() => requestPlanPanelOpen(), { message: 'Sign in to draw a defense plan' });
    }
  };

  return (
    <Fragment>
      {/* Shortcut letters go in `shortcut`, which the rail hover label
          shows as "Label (L)" while the accessible name stays the plain
          label. See useGlobalShortcuts.web.ts for what each letter does. */}
      <RailToolButton
        icon={Layers}
        label="Show map layers"
        shortcut="L"
        onPress={toggleLayersPanel}
        active={layersPanelOpen}
        testID="map-layers-button"
      />
      <RailToolButton
        icon={BookOpen}
        label="Show map key"
        shortcut="K"
        onPress={toggleMapKey}
        active={mapKeyShown}
        testID="map-rail-key"
      />
      <RailToolButton
        icon={Presentation}
        label="Present observations"
        shortcut="P"
        onPress={handlePresentPress}
        active={presentationActive}
        testID="map-slideshow-open"
      />
      <RailToolButton
        icon={Shield}
        label="Draw a defense plan"
        onPress={handlePlanPress}
        active={planPanelOpen || viewerOpen}
        testID="map-plan-open"
      />
      {canUsePeople && (
        <RailToolButton
          icon={Users}
          label="People"
          onPress={togglePeoplePanel}
          active={peoplePanelOpen}
          testID="map-people-button"
        />
      )}
      {canUseProgram && (
        <RailToolButton
          icon={ListChecks}
          label="Program"
          onPress={toggleProgramPanel}
          active={programPanelOpen}
          testID="map-program-button"
        />
      )}
    </Fragment>
  );
}
